import { useCallback, useEffect, useMemo, useState } from "react";
import { api, fetchBlob } from "../api/client";
import type { PassportDetail, Patient } from "../api/types";
import { AppleWalletMark } from "../components/AppleWalletMark";
import { PassportDrawer, confirmDeletePassport, scheduleSaveBody, type ScheduleSave } from "../components/PassportDrawer";
import { formatClock, formatDisplay } from "../lib/dates";
import { statusClass, statusLabel } from "../lib/status";
import { useAuth } from "../auth/AuthContext";

/** Same phrase as iOS PassportListStatus / Find Patient directory rows. */
const PASSPORT_PENDING = "Passport Pending";

type SortKey =
  | "passport_code"
  | "patient_name"
  | "patient_mrn"
  | "procedure"
  | "status"
  | "scheduled_or_at"
  | "facility_at"
  | "facility"
  | "encounter_at";

type SortDir = "asc" | "desc";

const TIME_KEYS = new Set<SortKey>(["scheduled_or_at", "facility_at", "encounter_at"]);

const COLUMNS: { key: SortKey; label: string }[] = [
  { key: "passport_code", label: "Surgical Passport" },
  { key: "patient_name", label: "Patient" },
  { key: "patient_mrn", label: "MRN" },
  { key: "procedure", label: "Procedure" },
  { key: "status", label: "Status" },
  { key: "scheduled_or_at", label: "Scheduled OR time" },
  { key: "facility_at", label: "Time to arrive" },
  { key: "facility", label: "Facility" },
  { key: "encounter_at", label: "Initial Encounter" },
];

type MainRow =
  | { key: string; kind: "passport"; passport: PassportDetail }
  | { key: string; kind: "patient"; patient: Patient };

function censusRows(passports: PassportDetail[], patients: Patient[]): MainRow[] {
  const active = passports.filter((p) => p.status !== "cancelled");
  const withPassport = new Set(active.map((p) => p.patient_id));
  const rows: MainRow[] = active.map((passport) => ({
    key: `p-${passport.id}`,
    kind: "passport",
    passport,
  }));
  for (const patient of patients) {
    if (withPassport.has(patient.id)) continue;
    rows.push({ key: `n-${patient.id}`, kind: "patient", patient });
  }
  return rows;
}

function rowName(row: MainRow): string {
  return row.kind === "passport" ? row.passport.patient_name || "" : row.patient.full_name || "";
}

function rowMrn(row: MainRow): string {
  return (row.kind === "passport" ? row.passport.patient_mrn : row.patient.mrn) || "";
}

function rowCode(row: MainRow): string {
  return row.kind === "passport" ? row.passport.passport_code || "" : "";
}

function rowProcedure(row: MainRow): string {
  if (row.kind === "patient") return PASSPORT_PENDING;
  return row.passport.procedure || "";
}

function rowStatus(row: MainRow): string {
  if (row.kind === "patient") return PASSPORT_PENDING;
  return statusLabel(row.passport);
}

function rowTime(row: MainRow, key: SortKey): string | null {
  if (row.kind === "patient") return null;
  if (key === "scheduled_or_at") return row.passport.scheduled_or_at ?? null;
  if (key === "facility_at") return row.passport.facility_at ?? null;
  return row.passport.encounter_at ?? null;
}

function sortText(row: MainRow, key: SortKey): string {
  if (key === "status") return rowStatus(row);
  if (key === "patient_mrn") return rowMrn(row);
  if (key === "procedure") return rowProcedure(row);
  if (key === "facility") return row.kind === "passport" ? row.passport.facility || "" : "";
  if (key === "patient_name") return rowName(row);
  if (key === "passport_code") return rowCode(row);
  return "";
}

function compareRows(a: MainRow, b: MainRow, key: SortKey, dir: SortDir): number {
  if (TIME_KEYS.has(key)) {
    const av = rowTime(a, key);
    const bv = rowTime(b, key);
    if (!av && !bv) return 0;
    if (!av) return 1;
    if (!bv) return -1;
    const diff = new Date(av).getTime() - new Date(bv).getTime();
    return dir === "asc" ? diff : -diff;
  }
  const as = sortText(a, key);
  const bs = sortText(b, key);
  if (!as && !bs) return 0;
  if (!as) return 1;
  if (!bs) return -1;
  const diff = as.localeCompare(bs, "en", { numeric: true, sensitivity: "base" });
  return dir === "asc" ? diff : -diff;
}

function matchesQuery(row: MainRow, q: string) {
  if (!q.trim()) return true;
  const needle = q.trim().toLowerCase();
  if (row.kind === "patient") {
    const p = row.patient;
    return [p.full_name, p.first_name, p.last_name, p.mrn, p.dob, PASSPORT_PENDING].some((v) =>
      String(v || "").toLowerCase().includes(needle),
    );
  }
  const item = row.passport;
  return [
    item.passport_code,
    item.patient_name,
    item.patient_first_name,
    item.patient_last_name,
    item.patient_mrn,
    item.procedure,
    item.surgeon_name,
    item.or_suite,
    item.facility,
    item.notes,
    item.status,
    statusLabel(item),
  ].some((v) => String(v || "").toLowerCase().includes(needle));
}

export function PassportsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState<MainRow[]>([]);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [detail, setDetail] = useState<PassportDetail | null>(null);
  const [printBusy, setPrintBusy] = useState("");
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("encounter_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const load = useCallback(async () => {
    const [passports, patients] = await Promise.all([
      api<PassportDetail[]>("/api/passports"),
      api<Patient[]>("/api/patients"),
    ]);
    setRows(censusRows(passports, patients));
    setError("");
  }, []);

  useEffect(() => {
    load().catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [load]);

  const list = useMemo(() => {
    const filtered = rows.filter((r) => matchesQuery(r, query));
    return [...filtered].sort((a, b) => compareRows(a, b, sortKey, sortDir));
  }, [rows, query, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(TIME_KEYS.has(key) ? "desc" : "asc");
  }

  async function openPassport(id: number) {
    const data = await api<PassportDetail>(`/api/portal/passports/${id}`);
    setDetail(data);
  }

  async function saveSuite(id: number, fields: ScheduleSave) {
    const data = await api<PassportDetail>(`/api/portal/passports/${id}/suite`, {
      method: "PATCH",
      body: JSON.stringify(scheduleSaveBody(fields)),
    });
    setDetail(data);
    await load();
  }

  async function printDoc(kind: "consent" | "report" | "record") {
    if (!detail) return;
    setPrintBusy(kind);
    try {
      const path =
        kind === "consent"
          ? `/api/passports/${detail.id}/consent/pdf`
          : kind === "report"
            ? `/api/passports/${detail.id}/report/pdf`
            : `/api/passports/${detail.id}/record/pdf`;
      const meta = await api<{ download_url: string }>(path, { method: "POST" });
      const blob = await fetchBlob(meta.download_url);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    } finally {
      setPrintBusy("");
    }
  }

  async function archiveCase() {
    if (!detail) return;
    setArchiveBusy(true);
    try {
      const data = await api<PassportDetail>(`/api/portal/passports/${detail.id}/complete`, {
        method: "POST",
      });
      setDetail(data);
      await load();
    } finally {
      setArchiveBusy(false);
    }
  }

  async function removePassport() {
    if (!detail) return;
    setDeleteBusy(true);
    try {
      if (await confirmDeletePassport(detail.passport_code, detail.id)) {
        setDetail(null);
        await load();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <>
      <div className="toolbar">
        <div>
          <strong>Main</strong>
          <span className="muted"> · {list.length}</span>
        </div>
        <div className="toolbar-actions">
          <input
            type="search"
            placeholder="Search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search"
          />
          <button type="button" className="tool-btn" onClick={() => load().catch((e) => setError(e.message))}>
            Refresh
          </button>
        </div>
      </div>
      {error && <div className="banner-error">{error}</div>}
      <div className="records-table-wrap">
        <table className="records-table">
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  aria-sort={sortKey === col.key ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                >
                  <button type="button" onClick={() => toggleSort(col.key)}>
                    {col.label}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {list.map((row) =>
              row.kind === "passport" ? (
                <tr key={row.key} onClick={() => void openPassport(row.passport.id)}>
                  <td className="code">
                    {row.passport.passport_code}
                    <AppleWalletMark show={row.passport.in_apple_wallet} />
                  </td>
                  <td>{row.passport.patient_name}</td>
                  <td>{row.passport.patient_mrn || "MRN pending"}</td>
                  <td>{row.passport.procedure || "—"}</td>
                  <td>
                    <span className={`status-pill ${statusClass(row.passport.status)}`}>
                      {statusLabel(row.passport)}
                    </span>
                  </td>
                  <td>{formatDisplay(row.passport.scheduled_or_at)}</td>
                  <td>{formatClock(row.passport.facility_at)}</td>
                  <td>{row.passport.facility || "—"}</td>
                  <td>{formatDisplay(row.passport.encounter_at)}</td>
                </tr>
              ) : (
                <tr key={row.key} className="is-pending">
                  <td className="code">—</td>
                  <td>{row.patient.full_name}</td>
                  <td>{row.patient.mrn || "MRN pending"}</td>
                  <td>{PASSPORT_PENDING}</td>
                  <td>
                    <span className="status-pill hold">{PASSPORT_PENDING}</span>
                  </td>
                  <td>—</td>
                  <td>—</td>
                  <td>—</td>
                  <td>—</td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
      {detail && (
        <PassportDrawer
          detail={detail}
          printBusy={printBusy}
          archiveBusy={archiveBusy}
          defaultSuite={detail.or_suite || ""}
          defaultSched={detail.scheduled_or_at || ""}
          defaultFacility={detail.facility || ""}
          defaultArrive={detail.facility_at || ""}
          onClose={() => setDetail(null)}
          onSave={saveSuite}
          onPrint={printDoc}
          onArchive={archiveCase}
          onDelete={isAdmin ? removePassport : undefined}
          deleteBusy={deleteBusy}
        />
      )}
    </>
  );
}
